<template>
  <v-container fluid class="pa-4">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-avatar size="56" class="mr-4 primary">
            <v-icon size="32" color="white">mdi-account-check</v-icon>
          </v-avatar>
          <div>
            <h1 class="display-1 mb-1 font-weight-bold">Select Enrollment</h1>
            <p class="subtitle-1 grey--text text--darken-1 mb-0">
              Choose an enrollment to view and manage member details
            </p>
          </div>
        </div>
      </v-col>
    </v-row>
    
    <!-- Loading State -->
    <v-card v-if="loading" class="mb-4" elevation="2">
      <v-card-text class="pa-6">
        <div class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            width="6"
            class="mb-4"
          ></v-progress-circular>
          <div class="title">Loading enrollments...</div>
          <div class="caption grey--text mt-2">Please wait while we fetch enrollment information</div>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Error State -->
    <v-card v-else-if="error" class="mb-4" elevation="2" outlined>
      <v-alert type="error" prominent border="left" colored-border>
        <v-alert-title class="headline">Error</v-alert-title>
        <div class="mt-2">{{ error }}</div>
      </v-alert>
    </v-card>
    
    <!-- Empty State -->
    <v-card v-else-if="enrollments.length === 0" class="mb-4" elevation="2" outlined>
      <v-card-text class="pa-6 text-center">
        <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-file-document-off</v-icon>
        <h2 class="headline mb-2">No Enrollments Found</h2>
        <p class="body-1 grey--text">
          No enrollments found. Please contact support if you believe this is an error.
        </p>
      </v-card-text>
    </v-card>
    
    <!-- Enrollments List -->
    <div v-else>
      <v-text-field
        v-model="search"
        label="Search enrollments"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        class="mb-4"
        clearable
      ></v-text-field>
      
      <v-row>
        <v-col
          v-for="enrollment in filteredEnrollments"
          :key="enrollment._id"
          cols="12"
          md="6"
          lg="4"
        >
          <v-card
            class="enrollment-card"
            elevation="3"
            outlined
            @click="selectEnrollment(enrollment)"
            style="cursor: pointer;"
          >
            <v-card-title class="primary white--text">
              <v-icon class="mr-2" color="white">mdi-file-document-outline</v-icon>
              <div class="flex-grow-1">
                <div class="text-h6 font-weight-bold">
                  {{ enrollment.primaryMemberName || 'Enrollment' }}
                </div>
                <div class="caption text--lighten-2 mt-1">
                  {{ formatDate(enrollment.createdAt || enrollment.enrollmentDate) }}
                </div>
              </div>
            </v-card-title>
            <v-card-text class="pa-4">
              <div class="mb-3">
                <div class="d-flex align-center mb-2">
                  <v-icon small class="mr-2 grey--text">mdi-email</v-icon>
                  <span class="body-2">{{ enrollment.email || 'N/A' }}</span>
                </div>
                <div class="d-flex align-center mb-2" v-if="enrollment.planName">
                  <v-icon small class="mr-2 grey--text">mdi-file-document</v-icon>
                  <span class="body-2 font-weight-medium">{{ enrollment.planName }}</span>
                </div>
                <div class="d-flex align-center mb-2" v-if="enrollment.memberCount">
                  <v-icon small class="mr-2 grey--text">mdi-account-group</v-icon>
                  <span class="body-2">{{ enrollment.memberCount }} member{{ enrollment.memberCount > 1 ? 's' : '' }}</span>
                </div>
                <div class="d-flex align-center" v-if="enrollment.status">
                  <v-chip
                    x-small
                    :color="getStatusColor(enrollment.status)"
                    text-color="white"
                  >
                    {{ enrollment.status }}
                  </v-chip>
                </div>
              </div>
            </v-card-text>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                outlined
                @click.stop="selectEnrollment(enrollment)"
              >
                <v-icon left>mdi-arrow-right</v-icon>
                View Details
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SelectEnrollment',
  data() {
    return {
      enrollments: [],
      loading: true,
      error: null,
      search: ''
    };
  },
  computed: {
    filteredEnrollments() {
      if (!this.search) return this.enrollments;
      
      const searchLower = this.search.toLowerCase();
      return this.enrollments.filter(enrollment => {
        const name = (enrollment.primaryMemberName || '').toLowerCase();
        const email = (enrollment.email || '').toLowerCase();
        const plan = (enrollment.planName || '').toLowerCase();
        const status = (enrollment.status || '').toLowerCase();
        
        return name.includes(searchLower) ||
               email.includes(searchLower) ||
               plan.includes(searchLower) ||
               status.includes(searchLower);
      });
    }
  },
  async mounted() {
    await this.loadEnrollments();
  },
  methods: {
    async loadEnrollments() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get('/api/enrollment/enrollments');
        
        // Transform the data to include display-friendly fields
        this.enrollments = (response.data || []).map(enrollment => {
          // Get primary member name from patients or enrollment data
          let primaryMemberName = '';
          if (enrollment.primaryMember) {
            primaryMemberName = `${enrollment.primaryMember.firstName || ''} ${enrollment.primaryMember.lastName || ''}`.trim();
          } else if (enrollment.firstName && enrollment.lastName) {
            primaryMemberName = `${enrollment.firstName} ${enrollment.lastName}`.trim();
          }
          
          return {
            _id: enrollment._id,
            primaryMemberName: primaryMemberName || 'Unknown',
            email: enrollment.email || '',
            planName: enrollment.planName || enrollment.selectedPlan || 'N/A',
            status: enrollment.status || 'Pending',
            createdAt: enrollment.createdAt || enrollment.enrollmentDate || new Date(),
            enrollmentDate: enrollment.enrollmentDate || enrollment.createdAt || new Date(),
            memberCount: enrollment.memberCount || 1
          };
        });
        
        // Sort by date (newest first)
        this.enrollments.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB - dateA;
        });
        
        if (this.enrollments.length === 0) {
          this.error = 'No enrollments found.';
        }
      } catch (error) {
        console.error('Error loading enrollments:', error);
        if (error.response) {
          this.error = `Error loading enrollments: ${error.response.data?.error || error.response.statusText}`;
        } else {
          this.error = 'Error loading enrollments. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },
    selectEnrollment(enrollment) {
      // Navigate to MemberEnrollment page with enrollmentId
      this.$router.push({
        name: 'member-enrollment',
        params: {
          enrollmentId: enrollment._id
        }
      });
    },
    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    getStatusColor(status) {
      const statusLower = (status || '').toLowerCase();
      if (statusLower === 'approved' || statusLower === 'active') return 'success';
      if (statusLower === 'pending') return 'warning';
      if (statusLower === 'draft') return 'info';
      if (statusLower === 'rejected' || statusLower === 'cancelled') return 'error';
      return 'grey';
    }
  }
};
</script>

<style scoped>
.enrollment-card {
  transition: all 0.3s ease;
  height: 100%;
}

.enrollment-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
}
</style>


